import { AUTH_TOKEN_CHANGED_EVENT } from '../constants/auth-events';
import { STORAGE_KEYS } from '../constants/storage-keys';

interface TokenSSEOptions {
  url: string;
  withCredentials?: boolean;
  readToken: () => string | null;
  authenticate: () => Promise<unknown>;
  attach: (source: EventSource) => void;
}

/** EventSource hides HTTP statuses: validate through the shared refresh flow first. */
export function startTokenSSE(options: TokenSSEOptions): () => void {
  let stopped = false;
  let generation = 0;
  let source: EventSource | null = null;
  let connecting = false;
  let retry: ReturnType<typeof setTimeout> | undefined;
  let expiry: ReturnType<typeof setTimeout> | undefined;
  let failures = 0;
  let rejectedToken: string | null = null;

  const close = () => {
    clearTimeout(retry); retry = undefined;
    clearTimeout(expiry); expiry = undefined;
    source?.close(); source = null;
  };

  const schedule = () => {
    if (stopped || retry || !options.readToken() || failures >= 6) return;
    const delay = Math.min(5000 * 2 ** failures++, 60000);
    retry = setTimeout(() => { retry = undefined; void connect(); }, delay);
  };

  const connect = async () => {
    if (stopped || connecting || source || navigator.onLine === false) return;
    const token = options.readToken();
    if (!token || token === rejectedToken) return;
    const ownGeneration = generation;
    connecting = true;
    try {
      // Uses the same Axios interceptor as normal API calls, including cookie refresh.
      await options.authenticate();
      if (stopped || ownGeneration !== generation) return;
      const current = options.readToken();
      if (!current || current === rejectedToken) return;
      const es = new EventSource(`${options.url}?token=${encodeURIComponent(current)}`, {
        withCredentials: options.withCredentials ?? false,
      });
      source = es;
      options.attach(es);
      es.onopen = () => { if (source === es) failures = 0; };
      es.onerror = () => {
        es.close(); // Disable native retries with the token captured in this URL.
        if (stopped || source !== es) return;
        source = null;
        clearTimeout(expiry); expiry = undefined;
        schedule();
      };
      // Revalidate at expiry even if the server keeps the stream open indefinitely.
      // JWT decoding is scheduling only; the server remains the authority on validity.
      try {
        const payload = current.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const expiresAt = Number(JSON.parse(atob(payload)).exp) * 1000;
        const delay = expiresAt - Date.now() + 1000;
        if (Number.isFinite(delay) && delay > 0) {
          expiry = setTimeout(() => {
            expiry = undefined;
            generation++; connecting = false; close(); void connect();
          }, Math.min(delay, 2147483647));
        }
      } catch { /* Missing/opaque expiry: server validation still protects connection. */ }
    } catch (error) {
      if (stopped || ownGeneration !== generation) return;
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 403) rejectedToken = options.readToken();
      else schedule();
    } finally {
      if (ownGeneration === generation) connecting = false;
    }
  };

  const tokenChanged = () => {
    generation++; connecting = false; failures = 0; close(); void connect();
  };
  const resume = () => {
    if (source || connecting || retry || document.visibilityState === 'hidden') return;
    failures = 0; void connect();
  };
  const storageChanged = (event: StorageEvent) => {
    if (event.key === STORAGE_KEYS.TOKEN || event.key === null) tokenChanged();
  };
  window.addEventListener(AUTH_TOKEN_CHANGED_EVENT, tokenChanged);
  window.addEventListener('storage', storageChanged);
  window.addEventListener('online', resume);
  document.addEventListener('visibilitychange', resume);
  void connect();
  return () => {
    stopped = true; generation++; close();
    window.removeEventListener(AUTH_TOKEN_CHANGED_EVENT, tokenChanged);
    window.removeEventListener('storage', storageChanged);
    window.removeEventListener('online', resume);
    document.removeEventListener('visibilitychange', resume);
  };
}
